import React, { useCallback, useState } from "react";
import { api, errMsg } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import Crest from "@/components/Crest";
import QrScanner from "@/components/island/QrScanner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, ShieldCheck, Loader2 } from "lucide-react";

// Officer dialog: scan ID-card QR to verify a brother; if `meeting` is given also marks attendance.
export default function QrCheckin({ open, onClose, meeting, onChecked }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);

  const handle = useCallback(async (code) => {
    setBusy(true);
    try {
      let r;
      if (meeting) {
        const { data } = await api.post(`/island/meetings/${meeting.id}/checkin`, { member_id: code });
        r = { ok: data.admitted, already: data.already, member: data.member, detail: data.detail || (data.already ? "Already marked present" : "Verified & marked present") };
        onChecked?.();
      } else {
        const id = code.includes("|") ? code.split("|")[1] : code;
        const { data } = await api.get(`/verify/${encodeURIComponent(id)}`);
        r = data.valid ? { ok: true, member: data, detail: "Verified IOTA Galleon member" } : { ok: false, member: data.name ? data : null, detail: data.name ? `Member is ${data.status}` : "Not a valid Member ID" };
      }
      setResult(r); setLog((l) => [{ ...r, at: new Date() }, ...l].slice(0, 20));
    } catch (e) { setResult({ ok: false, detail: errMsg(e) }); }
    finally { setBusy(false); }
  }, [meeting, onChecked]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setResult(null); onClose(); } }}>
      <DialogContent className="glass max-w-xl" data-testid="qr-checkin-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><ShieldCheck className="h-5 w-5 text-[hsl(var(--gold))]" />{meeting ? `QR check-in — ${meeting.title}` : "Verify a brother's ID card"}</DialogTitle></DialogHeader>
        <QrScanner onCode={handle} active={open} />
        {busy && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Checking…</p>}
        {result && (
          <div className={`flex items-center gap-3 rounded-xl border p-3 ${result.ok ? "border-emerald-500/50 bg-emerald-500/10" : "border-destructive/50 bg-destructive/10"}`} data-testid="qr-result">
            {result.ok ? <CheckCircle2 className="h-8 w-8 shrink-0 text-emerald-600" /> : <XCircle className="h-8 w-8 shrink-0 text-destructive" />}
            {result.member && (
              <Avatar className="h-12 w-12 ring-2 ring-[hsl(var(--gold))]/40">{result.member.photo_path ? <AuthImage path={result.member.photo_path} className="h-12 w-12 rounded-full object-cover" /> : <AvatarFallback>{result.member.name?.[0]}</AvatarFallback>}</Avatar>
            )}
            <div className="min-w-0 flex-1">
              {result.member && <p className="flex items-center gap-1.5 text-sm font-700"><Crest position={result.member.position} size={18} showRank={false} />{result.member.name} <span className="font-mono-data text-xs text-muted-foreground">{result.member.member_id}</span></p>}
              {result.member && <p className="text-xs text-muted-foreground">{result.member.position} · {result.member.frigate || "Galleon"}{result.member.deck_name ? ` · "${result.member.deck_name}"` : ""}</p>}
              <p className={`text-xs font-600 ${result.ok ? "text-emerald-700" : "text-destructive"}`} data-testid="qr-result-detail">{result.detail}</p>
            </div>
          </div>
        )}
        {log.length > 1 && (
          <div className="max-h-28 space-y-1 overflow-y-auto text-xs" data-testid="qr-log">
            {log.slice(1).map((l, i) => <p key={(l.at && l.at.getTime && l.at.getTime()) || i} className="flex items-center gap-1.5">{l.ok ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <XCircle className="h-3 w-3 text-destructive" />}{l.member?.name || "Unknown"} · {l.at.toLocaleTimeString()}{l.already && <Badge variant="outline" className="text-[0.55rem]">dup</Badge>}</p>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
